import { useRef } from 'react';
import { useSEO, useSchemaLD } from '@/hooks/useSEO';

const benefits = [
  {
    icon: 'ri-hotel-bed-line',
    title: 'Mobiliário que eleva a avaliação do hóspede no Booking e TripAdvisor',
    description: 'Hóspedes avaliam o quarto antes de qualquer outra coisa. Uma cama bem estruturada, mesa de trabalho funcional e armário bem acabado fazem a diferença entre 4 e 5 estrelas — e entre uma avaliação boa e uma excelente.',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Materiais resistentes ao uso intenso de hospedagem',
    description: 'MDF revestido em laminado HPL resistente a riscos, umidade e produtos de limpeza. Ferragens de alta durabilidade que suportam centenas de aberturas por mês. Mobiliário projetado para durar anos de uso intenso.',
  },
  {
    icon: 'ri-box-3-line',
    title: 'Projeto 3D gratuito de quartos, recepção e áreas comuns',
    description: 'Visualize cada quarto, a recepção e o lobby em 3D antes de fabricar. Aprovação completa do layout, cores e acabamentos — sem surpresas na entrega.',
  },
  {
    icon: 'ri-tools-line',
    title: 'Montagem sem interromper a operação do hotel',
    description: 'Nossa equipe própria instala quarto por quarto, no horário que o hotel preferir. Sem fechar o estabelecimento, sem atrapalhar hóspedes. Gestão de obra discreta e eficiente.',
  },
];

const blocks = [
  {
    title: 'Quartos que Transformam Hóspedes em Avaliações 5 Estrelas',
    text: [
      'O quarto de hotel é o produto principal. É onde o hóspede passa a maior parte do tempo, onde descansa, trabalha e forma a opinião que vai publicar no Booking, TripAdvisor e Google. Um quarto com mobiliário bem projetado — cama com cabeceira imponente, mesa de trabalho funcional, armário com espelho e iluminação interna — eleva a percepção de valor e justifica diárias mais altas.',
      'Nosso mobiliário para quartos de hotel inclui cabeceiras estofadas em courvin ou tecido com estrutura em MDF de alta espessura, criados-mudos com tampo em granito ou laminado HPL resistente a copos e produtos de higiene, mesas de trabalho com passa-fios integrado para carregadores e notebooks, armários com espelho de corpo inteiro, iluminação LED interna e ferragens silenciosas, e racks para TV com nichos organizados para controles e amenidades.',
    ],
    img: 'https://readdy.ai/api/search-image?query=Elegant%20hotel%20room%20interior%20with%20upholstered%20headboard%20bed%2C%20wooden%20nightstands%2C%20work%20desk%2C%20wardrobe%20with%20mirror%2C%20warm%20ambient%20lighting%2C%20neutral%20beige%20and%20wood%20tones%2C%20premium%20hospitality%20furniture%2C%205-star%20hotel%20bedroom%20design%2C%20professional%20photography&width=800&height=500&seq=hotel_v1_1&orientation=landscape',
    reverse: false,
  },
  {
    title: 'Recepção e Lobby que Criam a Primeira Impressão Perfeita',
    text: [
      'O hóspede forma a primeira impressão do hotel nos primeiros 30 segundos após entrar. O balcão de recepção, os sofás do lobby, a iluminação e os detalhes de acabamento comunicam imediatamente o nível do estabelecimento. Uma recepção bem projetada justifica a diária e cria expectativa positiva para o restante da estadia.',
      'Projetamos balcões de recepção com tampo em granito ou laminado premium, frente em MDF com acabamento em madeira ou laca, espaço interno organizado para computadores e documentos, sofás e poltronas de lobby em courvin ou tecido de alta durabilidade com estrutura em madeira maciça, mesas de centro e laterais com tampo em vidro temperado ou granito, e painéis de madeira com iluminação LED que valorizam o logo do hotel.',
    ],
    img: 'https://readdy.ai/api/search-image?query=Modern%20hotel%20lobby%20reception%20area%20with%20elegant%20front%20desk%2C%20comfortable%20lounge%20sofas%2C%20warm%20wood%20paneling%2C%20ambient%20LED%20lighting%2C%20neutral%20tones%2C%20premium%20hospitality%20interior%20design%2C%20welcoming%20atmosphere%2C%20professional%20photography&width=800&height=500&seq=hotel_v1_2&orientation=landscape',
    reverse: true,
  },
  {
    title: 'Áreas de Café da Manhã, Eventos e Convivência Completas',
    text: [
      'Além dos quartos, o hotel precisa de mobiliário funcional e elegante para o restaurante do café da manhã, salas de eventos, área de convivência e corredor. Cada ambiente precisa ser projetado para suportar o uso intenso da hospedagem sem perder o visual premium.',
      'Para o restaurante e café da manhã, oferecemos mesas em granito ou laminado HPL resistente a manchas e calor, cadeiras estofadas em courvin antimicrobiano de fácil limpeza, aparadores e buffets com tampo em granito e portas com fechamento suave. Para salas de eventos, mesas dobráveis e cadeiras empilháveis que otimizam o espaço. Para corredores, cômodas e espelhos que completam a experiência do hóspede.',
    ],
    img: 'https://readdy.ai/api/search-image?query=Hotel%20breakfast%20restaurant%20area%20with%20elegant%20tables%20and%20upholstered%20chairs%2C%20warm%20wood%20and%20neutral%20tones%2C%20buffet%20counter%20with%20granite%20top%2C%20ambient%20lighting%2C%20premium%20hospitality%20dining%20space%2C%20professional%20interior%20photography&width=800&height=500&seq=hotel_v1_3&orientation=landscape',
    reverse: false,
  },
];

const galleryImages = [
  {
    src: 'https://readdy.ai/api/search-image?query=Premium%20hotel%20room%20with%20upholstered%20headboard%2C%20wooden%20furniture%2C%20warm%20lighting%2C%20elegant%20hospitality%20interior%2C%205-star%20bedroom%20design&width=600&height=400&seq=hotel_g1&orientation=landscape',
    alt: 'Quarto de hotel com cabeceira estofada e mobiliário premium',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=Hotel%20reception%20desk%20with%20granite%20top%2C%20wood%20paneling%2C%20elegant%20lobby%20design%2C%20warm%20lighting%2C%20premium%20hospitality%20interior&width=600&height=400&seq=hotel_g2&orientation=landscape',
    alt: 'Recepção de hotel com balcão em granito',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=Hotel%20lobby%20lounge%20area%20with%20comfortable%20sofas%2C%20armchairs%2C%20coffee%20table%2C%20warm%20ambient%20lighting%2C%20elegant%20hospitality%20design&width=600&height=400&seq=hotel_g3&orientation=landscape',
    alt: 'Lobby de hotel com sofás e poltronas de design',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=Hotel%20breakfast%20dining%20area%20with%20elegant%20tables%20and%20chairs%2C%20buffet%20counter%2C%20warm%20wood%20tones%2C%20premium%20hospitality%20restaurant%20interior&width=600&height=400&seq=hotel_g4&orientation=landscape',
    alt: 'Área de café da manhã com mesas e cadeiras elegantes',
  },
];

const WA_LINK = 'https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Hotelaria%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!';

export default function HotelariaPage() {
  const whatsappBtnRef = useRef<HTMLAnchorElement>(null);

  useSEO({
    title: 'Mobiliário para Hotel e Pousada Caruaru PE | Hotelaria – Home Office Premium',
    description: 'Mobiliário para hotéis, pousadas e resorts em Caruaru PE. Quartos, recepção, lobby e restaurante. Materiais resistentes ao uso intenso, projeto 3D gratuito e montagem sem interromper a operação. Atendemos hotelaria no Agreste e Sertão de Pernambuco.',
    keywords: 'mobiliário hotel Recife, móveis pousada Petrolina, mobiliário hotelaria Garanhuns, quarto hotel Caruaru, recepção hotel Agreste PE, mobiliário resort Pernambuco, móveis hotel Nordeste, cabeceira hotel Recife, balcão recepção hotel Petrolina, mobiliário pousada Caruaru',
    path: '/hotelaria',
  });

  useSchemaLD({
    id: 'schema-hotelaria',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Mobiliário para Hotéis e Pousadas | Caruaru PE',
      description: 'Mobiliário para hotéis, pousadas e resorts: quartos, recepção, lobby e restaurante. Projeto 3D gratuito, equipe própria de montagem.',
      url: `${import.meta.env.VITE_SITE_URL ?? 'https://homeofficepremium.com'}/hotelaria`,
      about: {
        '@type': 'Service',
        name: 'Mobiliário para Hotelaria',
        provider: {
          '@type': 'Organization',
          name: 'Home Office Premium',
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'Av. Agamenon Magalhães, 1519',
            addressLocality: 'Caruaru',
            addressRegion: 'PE',
            postalCode: '55014-000',
            addressCountry: 'BR',
          },
        },
      },
    },
  });

  return (
    <div className="lp-page-wrapper min-h-screen bg-white">
      {/* Hero */}
      <section
        className="relative min-h-[640px] md:min-h-[700px] flex items-center"
        style={{
          backgroundImage: 'url(https://readdy.ai/api/search-image?query=Luxurious%20hotel%20room%20interior%20with%20elegant%20upholstered%20headboard%20bed%2C%20premium%20wooden%20furniture%2C%20warm%20ambient%20lighting%2C%20neutral%20beige%20and%20wood%20tones%2C%205-star%20hospitality%20design%2C%20professional%20photography%20wide%20angle&width=1400&height=700&seq=hotel_hero&orientation=landscape)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/55 to-black/20"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-28 pb-16 w-full">
          <div className="max-w-2xl">
            <span className="inline-block bg-[#E8610A] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-5">
              Hotéis · Pousadas · Resorts
            </span>
            <h1 className="text-white text-4xl md:text-5xl font-bold leading-tight mb-4">
              Mobiliário que transforma hóspedes em avaliações 5 estrelas.
            </h1>
            <p className="text-white/85 text-lg leading-relaxed mb-3">
              Quartos, recepção, lobby e restaurante projetados para impressionar. Materiais resistentes ao uso intenso da hospedagem, acabamento premium e projeto 3D gratuito.
            </p>
            <p className="text-white/70 text-sm mb-8">
              Projeto 3D gratuito · Montagem sem interromper a operação · Suporte local em Caruaru e Agreste
            </p>
            <a
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#E8610A] hover:bg-[#C4500A] text-white font-bold px-8 py-4 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide"
            >
              <i className="ri-whatsapp-line text-lg"></i>
              SOLICITAR ORÇAMENTO E PROJETO 3D GRATUITO
            </a>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-gray-900 text-2xl md:text-3xl font-bold">
              Por que hotéis e pousadas escolhem a <span className="text-[#E8610A]">Home Office Premium</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((b) => (
              <div key={b.title} className="bg-white rounded-xl p-6 flex flex-col gap-4 border border-gray-200">
                <div className="w-12 h-12 flex items-center justify-center bg-[#E8610A]/10 rounded-lg">
                  <i className={`${b.icon} text-[#E8610A] text-2xl`}></i>
                </div>
                <h3 className="text-gray-900 font-bold text-base leading-snug">{b.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{b.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Content Blocks */}
      <section className="py-16 bg-[#fafafa]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-gray-900 text-2xl md:text-3xl font-normal leading-tight">
              <span className="text-[#E8610A] font-semibold">Ambientes de Hotelaria</span> que Encantam do Check-in ao Check-out
            </h2>
            <div className="w-16 h-0.5 bg-[#E8610A] mx-auto mt-4"></div>
          </div>
          <div className="flex flex-col gap-12">
            {blocks.map((block, idx) => (
              <div key={idx} className={`flex flex-col ${block.reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-8 bg-white rounded-xl p-8 border border-gray-200`}>
                <div className="w-full lg:w-1/2 h-64 md:h-72 rounded-lg overflow-hidden flex-shrink-0">
                  <img src={block.img} alt={block.title} className="w-full h-full object-cover object-top" />
                </div>
                <div className="w-full lg:w-1/2">
                  <h3 className="text-gray-900 text-xl font-bold mb-4 leading-snug">{block.title}</h3>
                  <div className="flex flex-col gap-3">
                    {block.text.map((p, i) => (
                      <p key={i} className="text-gray-600 text-sm leading-relaxed">{p}</p>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-center mt-10">
            <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#E8610A] hover:bg-[#C4500A] text-white font-bold px-10 py-4 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide">
              <i className="ri-whatsapp-line text-lg"></i>
              SOLICITAR ORÇAMENTO E PROJETO 3D GRATUITO
            </a>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-gray-900 text-3xl md:text-4xl font-bold mb-8">
            Mais de <span className="text-[#E8610A]">10 anos</span> equipando hotéis e pousadas no Agreste
          </h2>
          <p className="text-gray-600 text-base leading-relaxed mb-5">
            A <strong>Home Office Premium</strong> entende que para um hotel, o mobiliário é parte do produto. Atendemos hotéis, pousadas e resorts em Caruaru, Recife e todo o Agreste Pernambucano — desde a pousada familiar que precisa renovar os quartos até o hotel de negócios que quer elevar o padrão da recepção e das áreas comuns.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-10">
            Nosso processo garante zero surpresas: visita técnica, projeto 3D para aprovação, fabricação com materiais resistentes ao uso intenso da hospedagem e instalação pela nossa equipe — quarto por quarto, sem interromper a operação. Você não precisa fechar o hotel para renovar o mobiliário.
          </p>
          <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 justify-center bg-[#E8610A] hover:bg-[#C4500A] text-white font-semibold px-10 py-4 rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide">
            <i className="ri-whatsapp-line text-lg"></i>
            FALAR COM UM ESPECIALISTA
          </a>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-20 bg-[#1a1a1a]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-[#E8610A] text-sm font-bold uppercase tracking-widest mb-4">Sem custo e sem compromisso</p>
          <h2 className="text-white text-3xl md:text-4xl font-bold leading-tight mb-4">
            Veja seus quartos e recepção em 3D antes de investir.
          </h2>
          <p className="text-gray-400 text-base mb-10">
            Nossa equipe visita seu hotel, entende o padrão desejado e entrega o projeto 3D completo em até 48h. Você aprova cada detalhe antes de fabricarmos — e a montagem é feita sem interromper a operação.
          </p>
          <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold px-10 py-4 rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide">
            <i className="ri-whatsapp-line text-lg"></i>
            QUERO MEU PROJETO 3D GRATUITO
          </a>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-16 bg-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-gray-400 text-sm mb-2">Galeria</p>
            <h2 className="text-white text-2xl md:text-3xl font-bold">
              Ambientes de <span className="text-[#E8610A]">Hotelaria Premium</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {galleryImages.map((img, idx) => (
              <div key={idx} className="w-full h-64 md:h-80 rounded-xl overflow-hidden">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-gray-400 text-xs font-semibold uppercase tracking-widest mb-3">SUPORTE LOCAL · CARUARU E AGRESTE PERNAMBUCANO</p>
          <h2 className="text-gray-900 text-4xl md:text-5xl font-bold mb-4">Solicite seu orçamento</h2>
          <p className="text-gray-500 text-base mb-8">Projeto 3D gratuito incluso. Resposta em até 2 horas.</p>
          <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 justify-center bg-[#25D366] hover:bg-[#1ebe5d] text-white font-semibold px-10 py-4 rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm mb-10">
            <i className="ri-whatsapp-line text-lg"></i>
            Chamar no WhatsApp
          </a>
          <div className="mt-10 text-gray-500 text-sm">
            <p>Av. Agamenon Magalhães, 1519 · Maurício de Nassau, Caruaru - PE · CEP 55014-000</p>
          </div>
        </div>
      </section>

      <footer className="bg-white py-8 border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-6 flex flex-col items-center gap-4">
          <img src="https://files.greatpages.com.br/arquivos/paginas_editor/98015-32d41c6ba8b126a2913863e7b342fb04.png" alt="Home Office Premium" className="h-16 w-auto object-contain" />
          <div className="flex items-center gap-4">
            <a href="https://www.instagram.com/homeoffice.premium/" target="_blank" rel="noopener noreferrer" className="cursor-pointer"><img src="https://files.greatpages.com.br/arquivos/paginas_editor/1-cc1918b73804ac63dbfe7624bf53ae73.png" alt="Instagram" className="w-8 h-8 object-contain" /></a>
            <a href="https://www.facebook.com/homeoffice.premium" target="_blank" rel="noopener noreferrer" className="cursor-pointer"><img src="https://files.greatpages.com.br/arquivos/paginas_editor/1-d39cee63d39805fee1ea3fe03b5a2e09.png" alt="Facebook" className="w-8 h-8 object-contain" /></a>
          </div>
        </div>
      </footer>

      <a ref={whatsappBtnRef} href={WA_LINK} target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-[9999] w-14 h-14 flex items-center justify-center bg-[#25D366] rounded-full cursor-pointer hover:scale-110 transition-transform duration-200" aria-label="WhatsApp" style={{ display: 'flex' }}>
        <i className="ri-whatsapp-line text-white text-2xl"></i>
      </a>
    </div>
  );
}
